package com.example.readingnotes.domain.usecase

import com.example.readingnotes.domain.model.Post
import com.example.readingnotes.domain.repository.PostRepository
import com.example.readingnotes.util.Result

class SearchPostsUseCase(
    private val postRepository: PostRepository,
) {
    suspend operator fun invoke(
        query: String,
        page: Int = 1,
        pageSize: Int = 20,
        sortBy: String? = null,
        status: String? = null,
        tags: Set<String> = emptySet(),
    ): Result<List<Post>> {
        // Use backend search API for full-text search
        // Status filtering is done on the backend
        // Tags and sorting are still done locally (not yet supported by backend)
        return try {
            // If query is blank, use regular getPosts with pagination
            val searchResult =
                if (query.isBlank()) {
                    // For empty query, get paginated posts
                    val postsResult = postRepository.getPosts(page, pageSize)
                    if (postsResult.isFailure) {
                        return Result.failure(postsResult.exceptionOrNull() ?: Exception("Unknown error"))
                    }
                    // Return posts directly, will apply filters below
                    postsResult.getOrThrow()
                } else {
                    // Use backend search API
                    val searchResult = postRepository.searchPosts(query, page, pageSize, status)
                    if (searchResult.isFailure) {
                        return Result.failure(searchResult.exceptionOrNull() ?: Exception("Search failed"))
                    }
                    searchResult.getOrThrow().results
                }

            // Apply tag filter if specified (backend doesn't support this yet)
            val filteredPosts =
                if (tags.isEmpty()) {
                    searchResult
                } else {
                    searchResult.filter { post ->
                        post.tags?.any { tag -> tags.contains(tag.name) } == true
                    }
                }

            // Apply sorting if specified (backend doesn't support this yet)
            val sortedPosts =
                when (sortBy) {
                    "oldest" -> filteredPosts.sortedBy { it.createdAt }
                    "title_asc" -> filteredPosts.sortedBy { it.title }
                    "title_desc" -> filteredPosts.sortedByDescending { it.title }
                    else -> filteredPosts.sortedByDescending { it.createdAt } // newest (default)
                }

            Result.success(sortedPosts)
        } catch (e: Exception) {
            Result.failure(e)
        }
    }
}
